import { Squares2X2Icon } from "@heroicons/react/24/outline";
import {
    activityCategories,
    ActivityCategory,
    activityCategoryLabels,
    ActivityTarget,
    activityTargetFromView,
    asActivityCategory,
    isAnySelected,
    TaskTypeId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferProcedureOutput } from "@trpc/server";
import { activityCategoryIcons } from "components/tasks/activity-category-icons";
import { ActivityTargetChips, AnyActivityChip } from "components/tasks/activity-chip";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { AutosavingInput } from "components/ui/autosaving-input";
import { ButtonSelect, ButtonSelectOption } from "components/ui/button-select";
import { PrimaryCompactButton } from "components/ui/primary-button";
import { Radio } from "components/ui/radio";
import { SecondaryButton, SecondaryCompactButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { useGetTaskTypesQuery } from "generated/graphql";
import React from "react";
import { stableQueryOptions } from "util/requests";
import {
    activityTargetViews,
    ActivityTypeOption,
    asCadenceUnitInput,
    cadenceUnitOptions,
    categoriesMissingTypeScope,
    describeInterval,
    EditableTouchpoint,
    emptyEditableTouchpoint,
    parseCadenceCount,
    scopedCategories,
    selectedArchivedTypesInCategory,
    selectedCategories,
    selectedTypeIdsInCategory,
    selectWholeCategory,
    suggestedTouchpointsForTier,
    toggleAny,
    toggleCategory,
    toggleTypeInCategory,
    typeOptionsForCategory,
    withoutCategory,
} from "./touchpoint-cadence";

type Touchpoint = inferProcedureOutput<
    AppRouter["marketingPlaybook"]["agencyPlaybook"]
>["touchpoints"][number];

const ANY_OPTION_ID = "any";

type CategoryScopeMode = "all" | "specific";

interface ActivityTypeOptions {
    options: ActivityTypeOption[];
    isResolved: boolean;
}

const useActivityTypeOptions = (): ActivityTypeOptions => {
    const taskTypeQuery = useGetTaskTypesQuery({}, stableQueryOptions());
    const taskTypes = taskTypeQuery.data?.getTaskTypes;
    return {
        options: (taskTypes ?? []).map(taskType => ({
            taskTypeId: taskType.id,
            name: taskType.name,
            category: asActivityCategory(taskType.category),
            isArchived: taskType.isArchived,
        })),
        isResolved: taskTypes !== undefined,
    };
};

const fromTouchpoint = (touchpoint: Touchpoint): EditableTouchpoint => ({
    targets: touchpoint.targets.map(activityTargetFromView),
    intervalUnit: asCadenceUnitInput(touchpoint.intervalUnit),
    intervalCount: touchpoint.intervalCount,
});

interface ScopeCardProps {
    category: ActivityCategory;
    activityTypes: ActivityTypeOption[];
    targets: ActivityTarget[];
    mode: CategoryScopeMode;
    onSelectMode: (mode: CategoryScopeMode) => void;
    onChange: (targets: ActivityTarget[]) => void;
}

const CategoryScopeCard: React.FC<ScopeCardProps> = ({
    category,
    activityTypes,
    targets,
    mode,
    onSelectMode,
    onChange,
}) => {
    const scopeGroupName = React.useId();
    const CategoryIcon = activityCategoryIcons[category];
    const categoryLabel = activityCategoryLabels[category];
    const offered = typeOptionsForCategory(activityTypes, category);
    const archivedInUse = selectedArchivedTypesInCategory(
        targets,
        category,
        activityTypes,
    );
    if (offered.length === 0 && archivedInUse.length === 0) {
        return null;
    }
    const typeOptions: ButtonSelectOption[] = [
        ...offered.map(option => ({ id: option.taskTypeId, label: option.name })),
        ...archivedInUse.map(option => ({
            id: option.taskTypeId,
            label: `${option.name} (archived)`,
        })),
    ];
    const selectedIds = selectedTypeIdsInCategory(targets, category, activityTypes);
    return (
        <section className="rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center gap-2 rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-1.5">
                <CategoryIcon className="h-4 w-4 text-gray-600" />
                <h4 className="text-xs font-semibold text-gray-900">{categoryLabel}</h4>
            </div>
            <div className="space-y-1 px-3 py-2 text-sm">
                <Radio
                    name={scopeGroupName}
                    checked={mode === "all"}
                    onChange={() => onSelectMode("all")}
                    label={`Any ${categoryLabel} type`}
                />
                <Radio
                    name={scopeGroupName}
                    checked={mode === "specific"}
                    onChange={() => onSelectMode("specific")}
                    label={`Specific ${categoryLabel} types`}
                />
                {mode === "specific" ? (
                    <div className="ml-6">
                        <ButtonSelect
                            options={typeOptions}
                            selectedIds={selectedIds}
                            onToggle={id =>
                                onChange(
                                    toggleTypeInCategory(
                                        targets,
                                        category,
                                        id as TaskTypeId,
                                        activityTypes,
                                    ),
                                )
                            }
                        />
                        {selectedIds.length === 0 ? (
                            <div className="pb-1 text-xs text-amber-600">
                                Pick at least one {categoryLabel} type.
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </section>
    );
};

interface EditCardProps {
    activityTypes: ActivityTypeOption[];
    value: EditableTouchpoint;
    onChange: (value: EditableTouchpoint) => void;
    footer: (state: { canSave: boolean }) => React.ReactNode;
}

const TouchpointEditCard: React.FC<EditCardProps> = ({
    activityTypes,
    value,
    onChange,
    footer,
}) => {
    const [specificCategories, setSpecificCategories] = React.useState<
        ActivityCategory[]
    >(() => scopedCategories(value.targets, activityTypes));
    const noTargetsSelected = value.targets.length === 0;
    const anySelected = isAnySelected(value.targets);
    const withTargets = selectedCategories(value.targets, activityTypes);
    const chosenCategories = activityCategories.filter(
        category =>
            withTargets.includes(category) || specificCategories.includes(category),
    );
    const unscoped = categoriesMissingTypeScope(
        value.targets,
        specificCategories,
        activityTypes,
    );
    const canSave = !noTargetsSelected && unscoped.length === 0;

    const selectMode = (category: ActivityCategory, mode: CategoryScopeMode) => {
        setSpecificCategories(current =>
            mode === "specific"
                ? [...current.filter(item => item !== category), category]
                : current.filter(item => item !== category),
        );
        onChange({
            ...value,
            targets:
                mode === "specific"
                    ? withoutCategory(value.targets, category, activityTypes)
                    : selectWholeCategory(value.targets, category, activityTypes),
        });
    };

    const toggleTopLevel = (id: string) => {
        if (id === ANY_OPTION_ID) {
            setSpecificCategories([]);
            onChange({ ...value, targets: toggleAny(value.targets) });
            return;
        }
        const category = asActivityCategory(id);
        if (chosenCategories.includes(category)) {
            setSpecificCategories(current => current.filter(item => item !== category));
            onChange({
                ...value,
                targets: withoutCategory(value.targets, category, activityTypes),
            });
            return;
        }
        onChange({
            ...value,
            targets: toggleCategory(value.targets, category, activityTypes),
        });
    };

    const buttonOptions: ButtonSelectOption[] = [
        { id: ANY_OPTION_ID, label: "Any", icon: Squares2X2Icon },
        ...activityCategories.map(category => ({
            id: category,
            label: activityCategoryLabels[category],
            icon: activityCategoryIcons[category],
        })),
    ];
    const selectedIds = anySelected
        ? [ANY_OPTION_ID, ...chosenCategories]
        : chosenCategories;

    return (
        <div className="space-y-3 rounded-md border border-gray-300 bg-gray-50 p-3 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
                <ButtonSelect
                    options={buttonOptions}
                    selectedIds={selectedIds}
                    onToggle={toggleTopLevel}
                />
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">every</span>
                    <div className="w-14 [&_input]:text-sm">
                        <AutosavingInput
                            type="number"
                            min={1}
                            initial={String(value.intervalCount)}
                            onCommit={input => {
                                const count = parseCadenceCount(input);
                                if (count !== null) {
                                    onChange({ ...value, intervalCount: count });
                                }
                            }}
                        />
                    </div>
                    <div className="w-24">
                        <Select
                            textSize="sm"
                            value={value.intervalUnit}
                            onChange={e =>
                                onChange({
                                    ...value,
                                    intervalUnit: asCadenceUnitInput(e.target.value),
                                })
                            }>
                            {cadenceUnitOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Select>
                    </div>
                </div>
            </div>
            {anySelected ? null : (
                <div className="space-y-2">
                    {chosenCategories.map(category => (
                        <CategoryScopeCard
                            key={category}
                            category={category}
                            activityTypes={activityTypes}
                            targets={value.targets}
                            mode={
                                specificCategories.includes(category) ? "specific" : "all"
                            }
                            onSelectMode={mode => selectMode(category, mode)}
                            onChange={targets => onChange({ ...value, targets })}
                        />
                    ))}
                </div>
            )}
            {canSave ? (
                <div className="flex flex-wrap items-center gap-1 text-sm text-gray-600">
                    <span>Counts:</span>
                    {anySelected ? (
                        <AnyActivityChip />
                    ) : (
                        <ActivityTargetChips
                            targets={activityTargetViews(value.targets, activityTypes)}
                        />
                    )}
                    <span className="font-medium text-gray-800">
                        {describeInterval(value.intervalCount, value.intervalUnit)}
                    </span>
                </div>
            ) : null}
            {noTargetsSelected ? (
                <div className="text-sm text-amber-600">
                    Pick at least one activity type for this schedule.
                </div>
            ) : null}
            {footer({ canSave })}
        </div>
    );
};

interface SavedRowProps {
    touchpoint: Touchpoint;
    activityTypes: ActivityTypeOption[];
    disabled: boolean;
    onSave: (id: string, value: EditableTouchpoint) => void;
    onDelete: (id: string) => void;
}

const SavedTouchpointRow: React.FC<SavedRowProps> = ({
    touchpoint,
    activityTypes,
    disabled,
    onSave,
    onDelete,
}) => {
    const [editing, setEditing] = React.useState(false);
    const [draft, setDraft] = React.useState<EditableTouchpoint>(() =>
        fromTouchpoint(touchpoint),
    );

    if (editing) {
        return (
            <TouchpointEditCard
                activityTypes={activityTypes}
                value={draft}
                onChange={setDraft}
                footer={({ canSave }) => (
                    <div className="flex items-center gap-2">
                        <PrimaryCompactButton
                            type="button"
                            disabled={!canSave}
                            onClick={() => {
                                onSave(touchpoint.id, draft);
                                setEditing(false);
                            }}>
                            Save
                        </PrimaryCompactButton>
                        <SecondaryCompactButton
                            type="button"
                            onClick={() => setEditing(false)}>
                            Cancel
                        </SecondaryCompactButton>
                        <div className="ml-auto">
                            <DeleteButton
                                backgroundColor="bg-gray-100"
                                size="sm"
                                onClick={() => onDelete(touchpoint.id)}
                            />
                        </div>
                    </div>
                )}
            />
        );
    }

    return (
        <div className="flex flex-wrap items-center gap-1">
            <ActivityTargetChips targets={touchpoint.targets} />
            <span className="text-sm text-gray-700">
                {describeInterval(
                    touchpoint.intervalCount,
                    asCadenceUnitInput(touchpoint.intervalUnit),
                )}
            </span>
            <EditButton
                backgroundColor="bg-white"
                size="sm"
                disabled={disabled}
                onClick={() => {
                    setDraft(fromTouchpoint(touchpoint));
                    setEditing(true);
                }}
            />
        </div>
    );
};

interface NewRowProps {
    activityTypes: ActivityTypeOption[];
    initial: EditableTouchpoint;
    onCreate: (value: EditableTouchpoint) => void;
    onCancel: () => void;
}

const NewTouchpointRow: React.FC<NewRowProps> = ({
    activityTypes,
    initial,
    onCreate,
    onCancel,
}) => {
    const [draft, setDraft] = React.useState<EditableTouchpoint>(initial);
    return (
        <TouchpointEditCard
            activityTypes={activityTypes}
            value={draft}
            onChange={setDraft}
            footer={({ canSave }) => (
                <div className="flex items-center gap-2">
                    <PrimaryCompactButton
                        type="button"
                        disabled={!canSave}
                        onClick={() => onCreate(draft)}>
                        Save
                    </PrimaryCompactButton>
                    <SecondaryCompactButton type="button" onClick={onCancel}>
                        Cancel
                    </SecondaryCompactButton>
                </div>
            )}
        />
    );
};

interface NewRow {
    key: string;
    initial: EditableTouchpoint;
}

interface Props {
    touchpoints: Touchpoint[];
    newRows: NewRow[];
    tierKey: string;
    disabled: boolean;
    onAddRow: (initial: EditableTouchpoint) => void;
    onSaveTouchpoint: (id: string, value: EditableTouchpoint) => void;
    onDeleteTouchpoint: (id: string) => void;
    onCreateTouchpoint: (newRowKey: string, value: EditableTouchpoint) => void;
    onCancelNewRow: (newRowKey: string) => void;
}

export const TierTouchpointsEditor: React.FC<Props> = ({
    touchpoints,
    newRows,
    tierKey,
    disabled,
    onAddRow,
    onSaveTouchpoint,
    onDeleteTouchpoint,
    onCreateTouchpoint,
    onCancelNewRow,
}) => {
    const { options: activityTypes, isResolved } = useActivityTypeOptions();
    const isEmpty = touchpoints.length === 0 && newRows.length === 0;
    const suggestions = suggestedTouchpointsForTier(tierKey);
    const editingDisabled = disabled || !isResolved;
    return (
        <div className="space-y-2 pl-1">
            <div className="text-sm font-medium text-gray-500">Scheduled activities</div>
            {isEmpty ? (
                <div className="text-sm text-gray-400">
                    Marketers will be prompted to reach out to their assigned referral
                    sources in this tier based on the activities you schedule. Pick the
                    categories that count, then narrow to specific activity types within
                    them if you want.
                </div>
            ) : (
                <div className="space-y-2">
                    {touchpoints.map(touchpoint => (
                        <SavedTouchpointRow
                            key={touchpoint.id}
                            touchpoint={touchpoint}
                            activityTypes={activityTypes}
                            disabled={editingDisabled}
                            onSave={onSaveTouchpoint}
                            onDelete={onDeleteTouchpoint}
                        />
                    ))}
                    {newRows.map(row => (
                        <NewTouchpointRow
                            key={row.key}
                            activityTypes={activityTypes}
                            initial={row.initial}
                            onCreate={value => onCreateTouchpoint(row.key, value)}
                            onCancel={() => onCancelNewRow(row.key)}
                        />
                    ))}
                </div>
            )}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <SecondaryButton
                    className="whitespace-nowrap"
                    disabled={editingDisabled}
                    onClick={() => onAddRow(emptyEditableTouchpoint())}>
                    Add activity
                </SecondaryButton>
                {isEmpty && suggestions.length > 0 ? (
                    <SecondaryButton
                        className="whitespace-nowrap"
                        disabled={editingDisabled}
                        onClick={() => suggestions.forEach(onAddRow)}>
                        Use recommended schedule
                    </SecondaryButton>
                ) : null}
            </div>
        </div>
    );
};
